import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import {
  Button,
  Form,
  Input,
  Label,
  Select,
  Textarea,
  ErrorText,
  Text,
} from "../../../styledComponent";
import styles from "./style.module.scss";
import { retailAgentApplicationInitialValues } from "../../../../utils/formik/initialvalue";
import { retailAgentFormValidationSchema } from "../../../../utils/formik/validationSchema";
import {
  normalizePhoneInput,
  countWords,
  countCharacters,
} from "../../../../utils/helpers";
import { BASE_URL, POST_API_URLS } from "../../../../utils/apiConstant";
import axios from "axios";
import { toast } from "react-toastify";

const BecomeRetailAgentForm = (props) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const formikRef = useRef(null);
  const formik = useFormik({
    initialValues: retailAgentApplicationInitialValues,
    validationSchema: retailAgentFormValidationSchema,
    onSubmit: (values) => formSubmitHandler(values),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      formikRef.current = formik;
      formikRef.current.setFieldValue("phone", phoneNumber);
    }, 1000);

    return () => clearTimeout(timer);
  }, [phoneNumber, formik]);

  // Transform form data to match API payload structure
  const transformFormDataToAPI = (formData) => {
    const hasSupport = formData.support_in_running_site === "yes";
    const trainingAgree = formData.willing_to_participate_training === "yes";
    const hasReferral = formData.referral_available === "yes";
    const hasCitizenshipOrPR = formData.canadian_citizenship_or_pr === "yes";

    // Convert total_investment_amount to number if it's a valid number string
    let investmentAmount = formData.total_investment_amount || "";
    if (investmentAmount && !isNaN(investmentAmount)) {
      investmentAmount = parseFloat(investmentAmount);
    } else if (investmentAmount === "") {
      investmentAmount = 0;
    }

    return {
      first_name: formData.first_name || "",
      last_name: formData.last_name || "",
      email: formData.email || "",
      phone: formData.phone || "",
      message: formData.message || "",
      experience_text: formData.previous_experience || "",
      funding_text: formData.funding_overview || "",
      total_investment_amount: investmentAmount,
      support_flag: hasSupport,
      support_details: hasSupport ? formData.support_details || "" : "",
      preferred_area: formData.preferred_geographical_area || "",
      training_agree: trainingAgree,
      referral_flag: hasReferral,
      referral_details: hasReferral ? formData.referral_details || "" : "",
      canadian_citizenship_or_pr: hasCitizenshipOrPR,
    };
  };

  async function formSubmitHandler(data) {
    props.loader(true);

    try {
      // Transform form data to match API payload structure
      const apiPayload = transformFormDataToAPI(data);

      const apiResponse = await axios.post(
        `${BASE_URL + POST_API_URLS.RETAIL_AGENT_FORM}`,
        apiPayload
      );
      props.loader(false);
      apiResponse && props.success(true);
    } catch (error) {
      console.log("error", error?.response);
      props.loader(false);
      toast.error(
        error?.response?.data?.message || "Error while submitting Form",
        {
          position: "top-right",
          autoClose: 1500,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: "colored",
          style: { fontSize: "1.6rem", fontWeight: 500 },
        }
      );
    }
  }

  const hasSupport = formik.values.support_in_running_site === "yes";
  const hasReferral = formik.values.referral_available === "yes";

  return (
    <Form
      onSubmit={formik.handleSubmit}
      className={styles.becomeRetailAgentForm}
    >
      {/* Left Column */}
      <div className={styles.leftColumn}>
        {/* First Name and Last Name */}
        <div className={styles.nameRow}>
          <div>
            <Label htmlFor="first_name">First Name*</Label>
            <Input
              type="text"
              id="first_name"
              maxLength={15}
              {...formik.getFieldProps("first_name")}
              style={
                formik.touched.first_name &&
                formik.errors.first_name && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            />
            {formik.touched.first_name && formik.errors.first_name && (
              <ErrorText className={styles.errorText}>
                {formik.errors.first_name}
              </ErrorText>
            )}
          </div>
        </div>
        <div>
          <div>
            <Label htmlFor="last_name">Last Name*</Label>
            <Input
              type="text"
              id="last_name"
              maxLength={15}
              {...formik.getFieldProps("last_name")}
              style={
                formik.touched.last_name &&
                formik.errors.last_name && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            />
            {formik.touched.last_name && formik.errors.last_name && (
              <ErrorText className={styles.errorText}>
                {formik.errors.last_name}
              </ErrorText>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <div>
            <Label htmlFor="email">Email*</Label>
            <Input
              type="email"
              id="email"
              {...formik.getFieldProps("email")}
              style={
                formik.touched.email &&
                formik.errors.email && { boxShadow: "0px 0px 0px 2px red" }
              }
            />
            {formik.touched.email && formik.errors.email && (
              <ErrorText className={styles.errorText}>
                {formik.errors.email}
              </ErrorText>
            )}
          </div>
        </div>

        {/* Phone Number */}
        <div>
          <div>
            <Label htmlFor="phone">Phone Number*</Label>
            <Input
              type="text"
              name="phone"
              id="phone"
              placeholder="xxx-xxx-xxxx"
              maxLength={12}
              onBlur={formik.handleBlur("phone")}
              onChange={(e) => {
                // Only allow digits and dashes
                const input = e.target.value.replace(/[^\d-]/g, "");
                setPhoneNumber((prev) => normalizePhoneInput(input, prev));
              }}
              value={phoneNumber}
              style={
                formik.touched.phone &&
                formik.errors.phone && { boxShadow: "0px 0px 0px 2px red" }
              }
            />
            {formik.touched.phone && formik.errors.phone && (
              <ErrorText className={styles.errorText}>
                {formik.errors.phone}
              </ErrorText>
            )}
          </div>
        </div>

        {/* Message */}
        <div>
          <div>
            <Label htmlFor="message">Message*</Label>
            <Textarea
              id="message"
              rows={4}
              maxLength={250}
              {...formik.getFieldProps("message")}
              style={
                formik.touched.message &&
                formik.errors.message && { boxShadow: "0px 0px 0px 2px red" }
              }
            />
            <div className={styles.charCount}>
              Characters: {countCharacters(formik.values.message)}/250
            </div>
            {formik.touched.message && formik.errors.message && (
              <ErrorText className={styles.errorText}>
                {formik.errors.message}
              </ErrorText>
            )}
          </div>
        </div>

        {/* Canadian Citizenship or PR Card */}
        <div>
          <div>
            <Label htmlFor="canadian_citizenship_or_pr">
              Do you have Canadian citizenship or a PR card?*
            </Label>
            <Select
              id="canadian_citizenship_or_pr"
              {...formik.getFieldProps("canadian_citizenship_or_pr")}
              style={
                formik.touched.canadian_citizenship_or_pr &&
                formik.errors.canadian_citizenship_or_pr && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
            {formik.touched.canadian_citizenship_or_pr &&
              formik.errors.canadian_citizenship_or_pr && (
                <ErrorText className={styles.errorText}>
                  {formik.errors.canadian_citizenship_or_pr}
                </ErrorText>
              )}
          </div>
        </div>

        {/* Previous Experience */}
        <div>
          <div>
            <Label htmlFor="previous_experience">
              Do you have any previous experience managing or operating a gas
              station or other business?*
            </Label>
            <Text
              $size="1.4rem"
              $color="var(--primaryGreyColor)"
              style={{ marginBottom: "1rem" }}
            >
              Please describe any relevant experience, including your role,
              responsibilities and achievements.
            </Text>
            <Textarea
              id="previous_experience"
              rows={6}
              {...formik.getFieldProps("previous_experience")}
              style={
                formik.touched.previous_experience &&
                formik.errors.previous_experience && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            />
            <div className={styles.wordCount}>
              Words: {countWords(formik.values.previous_experience)}/200
            </div>
            {formik.touched.previous_experience &&
              formik.errors.previous_experience && (
                <ErrorText className={styles.errorText}>
                  {formik.errors.previous_experience}
                </ErrorText>
              )}
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className={styles.rightColumn}>
        {/* Funding Overview */}
        <div>
          <div>
            <Label htmlFor="funding_overview">
              What funding do you have available to invest in this opportunity?*
            </Label>
            <Text
              $size="1.4rem"
              $color="var(--primaryGreyColor)"
              style={{ marginBottom: "1rem" }}
            >
              Please provide an overview of the funds or financing you have
              direct access to in order to support this business venture. Please
              total the amount at the end. Note: If you progress through the
              selection process, we will require proof of funds (e.g., bank
              statements, financing approvals, or other supporting
              documentation).
            </Text>
            <Textarea
              id="funding_overview"
              rows={4}
              maxLength={250}
              {...formik.getFieldProps("funding_overview")}
              style={
                formik.touched.funding_overview &&
                formik.errors.funding_overview && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            />
            <div className={styles.charCount}>
              Characters: {countCharacters(formik.values.funding_overview)}/250
            </div>
            {formik.touched.funding_overview &&
              formik.errors.funding_overview && (
                <ErrorText className={styles.errorText}>
                  {formik.errors.funding_overview}
                </ErrorText>
              )}
          </div>
        </div>

        {/* Total Investment Amount */}
        <div>
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                marginBottom: "1rem",
              }}
            >
              <Label
                htmlFor="total_investment_amount"
                style={{ marginBottom: 0 }}
              >
                Total Investment Amount*
              </Label>
              <Text $size="1.4rem" $color="var(--primaryGreyColor)">
                (amount in CAD)
              </Text>
            </div>
            <Input
              type="text"
              id="total_investment_amount"
              placeholder="Enter amount"
              {...formik.getFieldProps("total_investment_amount")}
              onChange={(e) => {
                // Only allow digits
                const value = e.target.value.replace(/[^\d]/g, "");
                formik.setFieldValue("total_investment_amount", value);
              }}
              style={
                formik.touched.total_investment_amount &&
                formik.errors.total_investment_amount && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            />
            {formik.touched.total_investment_amount &&
              formik.errors.total_investment_amount && (
                <ErrorText className={styles.errorText}>
                  {formik.errors.total_investment_amount}
                </ErrorText>
              )}
          </div>
        </div>

        {/* Support in Running Site */}
        <div>
          <div>
            <Label htmlFor="support_in_running_site">
              Do you have any support in running the site?*
            </Label>
            <Text
              $size="1.4rem"
              $color="var(--primaryGreyColor)"
              style={{ marginBottom: "1rem" }}
            >
              Will any business partners, family members, or others be involved
              in the daily operations or management?
            </Text>
            <Select
              id="support_in_running_site"
              {...formik.getFieldProps("support_in_running_site")}
              style={
                formik.touched.support_in_running_site &&
                formik.errors.support_in_running_site && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
            {formik.touched.support_in_running_site &&
              formik.errors.support_in_running_site && (
                <ErrorText className={styles.errorText}>
                  {formik.errors.support_in_running_site}
                </ErrorText>
              )}
          </div>
        </div>

        {/* Support Details - Conditional */}
        {hasSupport && (
          <div>
            <div>
              <Label htmlFor="support_details">Please provide details*</Label>
              <Input
                type="text"
                id="support_details"
                maxLength={100}
                {...formik.getFieldProps("support_details")}
                style={
                  formik.touched.support_details &&
                  formik.errors.support_details && {
                    boxShadow: "0px 0px 0px 2px red",
                  }
                }
              />
              <div className={styles.charCount}>
                Characters: {countCharacters(formik.values.support_details)}/100
              </div>
              {formik.touched.support_details &&
                formik.errors.support_details && (
                  <ErrorText className={styles.errorText}>
                    {formik.errors.support_details}
                  </ErrorText>
                )}
            </div>
          </div>
        )}

        {/* Preferred Geographical Area */}
        <div>
          <div>
            <Label htmlFor="preferred_geographical_area">
              What is your preferred geographical area or city for operating a
              Canco location?*
            </Label>
            <Input
              type="text"
              id="preferred_geographical_area"
              maxLength={100}
              {...formik.getFieldProps("preferred_geographical_area")}
              style={
                formik.touched.preferred_geographical_area &&
                formik.errors.preferred_geographical_area && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            />
            <div className={styles.charCount}>
              Characters:{" "}
              {countCharacters(formik.values.preferred_geographical_area)}/100
            </div>
            {formik.touched.preferred_geographical_area &&
              formik.errors.preferred_geographical_area && (
                <ErrorText className={styles.errorText}>
                  {formik.errors.preferred_geographical_area}
                </ErrorText>
              )}
          </div>
        </div>

        {/* Willing to Participate in Training */}
        <div>
          <div>
            <Label htmlFor="willing_to_participate_training">
              Canco has a Retailer Training Program – are you willing to
              participate?*
            </Label>
            <Text
              $size="1.4rem"
              $color="var(--primaryGreyColor)"
              style={{ marginBottom: "1rem" }}
            >
              This program is designed to fully prepare you to operate your own
              Canco location. (Note: The training typically runs 3–6 weeks and
              the cost is the responsibility of the agent.)
            </Text>
            <Select
              id="willing_to_participate_training"
              {...formik.getFieldProps("willing_to_participate_training")}
              style={
                formik.touched.willing_to_participate_training &&
                formik.errors.willing_to_participate_training && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
            {formik.touched.willing_to_participate_training &&
              formik.errors.willing_to_participate_training && (
                <ErrorText className={styles.errorText}>
                  {formik.errors.willing_to_participate_training}
                </ErrorText>
              )}
          </div>
        </div>

        {/* Referral Available */}
        <div>
          <div>
            <Label htmlFor="referral_available">
              Would you be able to provide a referral name and phone number as a
              reference from a previous employer or colleague?*
            </Label>
            <Select
              id="referral_available"
              {...formik.getFieldProps("referral_available")}
              style={
                formik.touched.referral_available &&
                formik.errors.referral_available && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
            {formik.touched.referral_available &&
              formik.errors.referral_available && (
                <ErrorText className={styles.errorText}>
                  {formik.errors.referral_available}
                </ErrorText>
              )}
          </div>
        </div>

        {/* Referral Details - Conditional */}
        {hasReferral && (
          <div>
            <div>
              <Label htmlFor="referral_details">
                Please provide referral details*
              </Label>
              <Input
                type="text"
                id="referral_details"
                maxLength={100}
                {...formik.getFieldProps("referral_details")}
                style={
                  formik.touched.referral_details &&
                  formik.errors.referral_details && {
                    boxShadow: "0px 0px 0px 2px red",
                  }
                }
              />
              <div className={styles.charCount}>
                Characters: {countCharacters(formik.values.referral_details)}
                /100
              </div>
              {formik.touched.referral_details &&
                formik.errors.referral_details && (
                  <ErrorText className={styles.errorText}>
                    {formik.errors.referral_details}
                  </ErrorText>
                )}
            </div>
          </div>
        )}
      </div>

      {/* Submit Button - Spans both columns */}
      <Button
        $width="100%"
        type="submit"
        className={
          !(formik.isValid && formik.dirty) ? "disabledBtn" : "submitBtn"
        }
      >
        Submit
      </Button>
    </Form>
  );
};

export default BecomeRetailAgentForm;
